import { useSuggestedArticle } from 'app/context/SuggestedArticleProvider';
import {
  useSetSuggestedArticleClosed,
  useSetSuggestedArticleShown,
  useSetSuggestedArticleVisited,
} from 'app/hooks/mutate/usePatchSuggestedArticle';
import { useButtonMessages } from 'app/hooks/useButtonMessages';
import useCustomization from 'app/hooks/useCustomization';
import useFeatureFlags from 'app/hooks/useFeatureFlags';
import useHandleSearchResultClick from 'app/modules/tailor/hooks/useHandleSearchResultClick';
import { usePersistedLocation } from 'app/modules/tailor/hooks/usePersistedLocation';
import useSuggestedArticleTracking from 'app/modules/tailor/hooks/useSuggestedArticleTracking';
import { useCallback, useEffect, useState } from 'react';
import { useChatBubbleScrollBehavior } from 'app/hooks/useChatBubbleScrollBehavior';
import useIsMobile from 'app/hooks/useIsMobile';

import GenericV2Button from './GenericV2Button';
import TailorCta from './TailorCta';

const CombinedWebchatButton = ({
  onClick,
  onClickCloseChatBubble,
  chatBubbleOpen,
  unreadMessageCount,
}: {
  onClick: () => void;
  onClickCloseChatBubble: () => void;
  chatBubbleOpen: boolean;
  unreadMessageCount: number;
}) => {
  const isMobile = useIsMobile();
  const customization = useCustomization();
  const [timerCompleted, setTimerCompleted] = useState(false);
  const [scrollCompleted, setScrollCompleted] = useState(false);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [nbaLoadTime, setNbaLoadTime] = useState<number | null>(null);

  const { suggestedArticle, lastViewedArticle, setLastViewedArticle, isLoading } = useSuggestedArticle();
  const { captureSuggestedTopicAppearance, captureSuggestedTopicClosed, captureSuggestedTopicClicked } =
    useSuggestedArticleTracking();
  const { mutate: setSuggestedArticleShown } = useSetSuggestedArticleShown();
  const { mutate: setSuggestedArticleClosed } = useSetSuggestedArticleClosed();
  const { mutate: setSuggestedArticleVisited } = useSetSuggestedArticleVisited();
  const handleSearchResultClick = useHandleSearchResultClick();
  const [, , removePersistedLocation] = usePersistedLocation();
  const messages = useButtonMessages();
  const featureFlags = useFeatureFlags();

  const buttonIconSize = {
    width: customization?.open_button_icon_width || 52,
    height: customization?.open_button_icon_height || 52,
  };

  const actionIconSize = {
    width: customization?.open_button_action_icon_width || 52,
    height: customization?.open_button_action_icon_height || 52,
  };

  const showSuggestedArticle =
    suggestedArticle &&
    (!!lastViewedArticle?.external_link || !!lastViewedArticle?.resource_link) &&
    !suggestionDismissed;

  const [trackedArticles, setTrackedArticles] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isExpanded || (isMobile && customization?.open_button_fab_disable_transformations)) return;

    const timer = setTimeout(() => {
      setTimerCompleted(true);
      const shouldExpandImmediately =
        (!isMobile && !isLoading) || (isMobile && !isLoading && !customization?.open_button_expand_on_scroll);

      if (shouldExpandImmediately) {
        setIsExpanded(true);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [
    isLoading,
    isMobile,
    customization?.open_button_expand_on_scroll,
    customization?.open_button_fab_disable_transformations,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!chatBubbleOpen) return;
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const totalHeight = document.documentElement.scrollHeight;
      if (scrollPosition / totalHeight >= 0.6) {
        setScrollCompleted(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };
    if (document.documentElement.scrollHeight > window.innerHeight) {
      window.addEventListener('scroll', handleScroll);
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, [chatBubbleOpen]);

  useEffect(() => {
    if (isLoading || !suggestedArticle || !lastViewedArticle || !chatBubbleOpen) return;

    const articleId = suggestedArticle.id;
    if (trackedArticles.has(articleId)) return;

    if (timerCompleted || scrollCompleted) {
      setSuggestedArticleShown();
      const nbaLoadTime = Date.now();
      setNbaLoadTime(nbaLoadTime);
      captureSuggestedTopicAppearance(suggestedArticle, lastViewedArticle, 'nonWidget');
      setTrackedArticles((prev) => new Set(prev).add(articleId));
    }
  }, [
    timerCompleted,
    scrollCompleted,
    isLoading,
    chatBubbleOpen,
    captureSuggestedTopicAppearance,
    lastViewedArticle,
    setSuggestedArticleShown,
    suggestedArticle,
    trackedArticles,
  ]);

  const handleSuggestionClick = () => {
    if (!suggestedArticle) return;

    setSuggestedArticleVisited();

    const timeSinceNbaLoadInSecconds = nbaLoadTime ? (Date.now() - nbaLoadTime) / 1000 : 0;
    captureSuggestedTopicClicked(suggestedArticle, lastViewedArticle, 'nonWidget', timeSinceNbaLoadInSecconds);

    if (!suggestedArticle.resource_link && !suggestedArticle.external_link) {
      onClick();
    }

    setTimeout(() => {
      handleSearchResultClick({
        result: suggestedArticle,
        resultIndex: 0,
        isSuggested: false,
        linkDetails: { source: 'nonWidget' },
        linkLocation: 'nbr',
      });
    }, 100);
  };

  const handleCloseBubble = () => {
    setIsExpanded(false);
    onClickCloseChatBubble();
    if (showSuggestedArticle) {
      setSuggestedArticleClosed();
      captureSuggestedTopicClosed(suggestedArticle, lastViewedArticle, 'nonWidget');
    }
  };

  const handleDismissNba = () => {
    handleCloseBubble();
    setLastViewedArticle(null);
    setSuggestionDismissed(true);
    removePersistedLocation();
  };

  // collapse NBR after scroll -----------------
  const handleScroll = useCallback(
    (open: boolean) => {
      if (!timerCompleted) return;

      setIsExpanded(open);
    },
    [timerCompleted]
  );

  useChatBubbleScrollBehavior(handleScroll);

  if (featureFlags.uiRefreshNextBestResourceCta) {
    return (
      <TailorCta
        messages={messages}
        suggestedArticle={suggestedArticle}
        showSuggestedArticle={!!showSuggestedArticle}
        isExpanded={isExpanded}
        onClick={showSuggestedArticle ? handleSuggestionClick : onClick}
        onClickCloseChatBubble={showSuggestedArticle ? handleDismissNba : handleCloseBubble}
        unreadMessageCount={unreadMessageCount}
      />
    );
  }

  const buttonProps = {
    id: showSuggestedArticle ? 'ostro-suggested-article-button' : 'ostro-webchat-button',
    onClick: showSuggestedArticle ? handleSuggestionClick : onClick,
    onClickCloseChatBubble: showSuggestedArticle ? handleDismissNba : handleCloseBubble,
    buttonIcon: customization?.open_button_icon || '',
    actionIcon: showSuggestedArticle ? customization?.open_button_action_icon : customization?.open_button_icon,
    isExpanded: isExpanded,
    expandedSize: (showSuggestedArticle && suggestedArticle?.description ? 'lg' : 'md') as 'lg' | 'md',
    unreadMessageCount,
    buttonIconSize,
    actionIconSize: showSuggestedArticle ? actionIconSize : buttonIconSize,
    textClassName: showSuggestedArticle ? 'font-bold text-left' : 'text-center',
    suggestedArticleTitle: suggestedArticle?.title || '',
    messages: showSuggestedArticle
      ? [{ text: suggestedArticle?.title || '', subtext: suggestedArticle?.description || '' }]
      : messages,
    messageLineClamp: showSuggestedArticle && suggestedArticle?.description ? 1 : 2,
    prependText: showSuggestedArticle ? (
      <span
        id="ostro-suggested-article-next-text"
        className="text-brand-nextBestAction mr-2 text-sm font-bold"
      >
        Next
      </span>
    ) : undefined,
  };

  return <GenericV2Button {...buttonProps} />;
};

export default CombinedWebchatButton;
